import { type PriceEntry, type PriceTypes } from '../models/pricing.models';

export type { PriceEntry, PriceTypes };

interface ResolvedPrice<T> {
  priceType: PriceTypes;
  price: Pick<PriceEntry, 'value' | 'currency' | 'taxable'>;
  linkedItems: T[];
}

const DEFAULT_PRICE: Pick<PriceEntry, 'value' | 'currency' | 'taxable'> = {
  value: 0,
  currency: 'USD',
  taxable: true,
};

const ADDON_FALLBACK_MAP: Partial<Record<PriceTypes, PriceTypes>> = {
  STANDARD_ADDON: 'STANDARD',
  MEMBER_ADDON: 'MEMBER',
};

export function resolveProductPrice<T extends { priceType: string }>(
  requestedPriceType: PriceTypes,
  pricebookData:
    | { prices: PriceEntry[]; linkedItems: T[] }
    | undefined,
  fallback?: {
    price: number;
    currency: 'USD';
    taxable: boolean;
    linkedItems?: T[];
  }
): ResolvedPrice<T> {
  if (!pricebookData) {
    return {
      priceType: requestedPriceType,
      price: fallback
        ? {
            value: fallback.price,
            currency: fallback.currency,
            taxable: fallback.taxable,
          }
        : DEFAULT_PRICE,
      linkedItems: fallback?.linkedItems ?? [],
    };
  }

  const requestedPrice = pricebookData.prices.find(
    (p) => p.priceType === requestedPriceType
  );

  const basePriceType = ADDON_FALLBACK_MAP[requestedPriceType];
  const isAddonWithZeroPrice =
    basePriceType !== undefined &&
    (!requestedPrice || requestedPrice.value === 0);

  const priceType = isAddonWithZeroPrice ? basePriceType : requestedPriceType;

  const price = isAddonWithZeroPrice
    ? pricebookData.prices.find((p) => p.priceType === priceType) ??
      DEFAULT_PRICE
    : requestedPrice ?? DEFAULT_PRICE;

  const linkedItemFilterType =
    priceType === 'DYNAMIC' ? 'STANDARD' : priceType;

  return {
    priceType,
    price: { value: price.value, currency: price.currency, taxable: price.taxable },
    linkedItems: pricebookData.linkedItems.filter(
      (item) => item.priceType === linkedItemFilterType
    ),
  };
}
